import { VersionedTransaction } from '@solana/web3.js'
import { UNKNOWN_ERROR } from '../constants/swapErrors'
import { signSolMessage } from '../handlers/walletActions/wallet'
import { SolanaToken } from '../interfaces/common'
import {
  SwapProvider,
  SwapProviderQuote,
  SwapProviderQuoteFetchError,
} from '../interfaces/swapInterface'
import { Wallet } from '../providers/WalletProvider'
import { arrayBufferToBase64, base64ToArrayBuffer } from '../utils/arrayBuffer'

const API_URL = 'https://lite-api.jup.ag/ultra/v1'

interface RequestParams {
  [key: string]: string
}

class JupiterClient {
  async getQuote(
    amount: bigint,
    fromToken: SolanaToken,
    toToken: SolanaToken,
    taker: string
  ): Promise<SwapProviderQuote | SwapProviderQuoteFetchError> {
    try {
      const response = await this.#fetch('/order', {
        method: 'GET',
        params: {
          inputMint: fromToken.address,
          outputMint: toToken.address,
          amount: amount.toString(),
          taker,
        },
      })

      if (response.outAmount && response.transaction) {
        return {
          price: Number(response.outAmount),
          priceHuman: Number(response.outAmount) * 10 ** -toToken.decimals,
          provider: SwapProvider.JUPITER,
          slippage: Number(response.slippageBps) / 100,
          metadata: {
            jupiterOrder: response,
          },
        }
      } else {
        return { error: response.errorMessage || UNKNOWN_ERROR, provider: SwapProvider.JUPITER }
      }
    } catch (e) {
      return { error: UNKNOWN_ERROR, provider: SwapProvider.JUPITER }
    }
  }

  async swap(quote: SwapProviderQuote, wallet: Wallet) {
    const { transaction, requestId } = quote.metadata!.jupiterOrder
    const { message, signatures } = VersionedTransaction.deserialize(
      base64ToArrayBuffer(transaction)
    )

    const signatureAddresses = message.staticAccountKeys.map((k) => k.toString())
    const signatureIndex = signatureAddresses.indexOf(wallet.solAddress!)
    const signature = await signSolMessage(wallet.principalId, [...message.serialize()])
    signatures[signatureIndex] = Uint8Array.from(signature)

    const signedTransaction = new VersionedTransaction(message, signatures)

    const response = await this.#fetch('/execute', {
      method: 'POST',
      body: {
        signedTransaction: arrayBufferToBase64(signedTransaction.serialize()),
        requestId,
      },
    })

    if (response.code === 0) {
      return Number(response.inputAmountResult)
    } else {
      throw response.error || response
    }
  }

  async #fetch(
    path: string,
    {
      method,
      params,
      body,
    }: { method: 'GET' | 'POST'; params?: RequestParams; body?: RequestParams }
  ) {
    let url = API_URL + path
    if (params) {
      url += '?' + new URLSearchParams(params).toString()
    }

    const options: RequestInit = { method }
    if (body) {
      options.body = JSON.stringify(body)
      options.headers = { 'Content-Type': 'application/json' }
    }

    const result = await fetch(url, options)

    if (result.headers.get('Content-Type') === 'application/json') {
      return result.json()
    } else {
      throw await result.text()
    }
  }
}

export default JupiterClient
